import get from 'lodash/get';
import type {Data, DataValue} from 'schemaDefinition/types';

/** 'agents[1].roles' => ['agents', '1', '.roles'] */
const arrayItemPathRegex = /^(.*?)\[(\d+)\](.*)$/;

type ArrayItemPathParts = {
  /** Path to the array, e.g. 'agents' */
  arrayPath: string;
  /** Index of the item in the change request array, e.g. 1 */
  itemIndex: number;
  /** Path within the item, e.g. '.roles', or '' when the item itself is targeted */
  subPath: string;
};

const getArrayItemPathParts = (
  localPath: string,
): ArrayItemPathParts | undefined => {
  const match = localPath.match(arrayItemPathRegex);
  return match
    ? {arrayPath: match[1], itemIndex: parseInt(match[2]), subPath: match[3]}
    : undefined;
};

const getLinkEntityId = (value: DataValue | undefined): string | undefined => {
  const link = (value as {link?: {entityId?: string | null}} | undefined)?.link;
  return typeof link?.entityId === 'string' ? link.entityId : undefined;
};

export type ApprovedValueTarget =
  | {
      itemAction: 'replace';
      localPath: string;
      value: DataValue;
    }
  | {
      itemAction: 'addItem';
      localPath: string;
      value: DataValue;
    };

/**
 * Resolves where an approved value should be applied in the original entity.
 * Linked agents are matched by link.entityId
 */
export const resolveApprovedValueTarget = (
  entity: Data,
  changeRequestEntity: Data,
  localPath: string,
  value: DataValue,
): ApprovedValueTarget => {
  const parts = getArrayItemPathParts(localPath);

  if (!parts) {
    return {itemAction: 'replace', localPath, value};
  }

  const {arrayPath, itemIndex, subPath} = parts;
  const itemPath = `${arrayPath}[${itemIndex}]`;
  const proposedItem = get(changeRequestEntity, itemPath) as
    | DataValue
    | undefined;
  const originalArray = get(entity, arrayPath) as DataValue[] | undefined;
  const originalItems = Array.isArray(originalArray) ? originalArray : [];

  // Locate linked value (agent) by entity id, as indexes may differ
  const proposedEntityId = getLinkEntityId(proposedItem);
  const originalIndex = proposedEntityId
    ? originalItems.findIndex(
        item => getLinkEntityId(item) === proposedEntityId,
      )
    : // Non-linked values, or values without an entity id, are matched by index,
      // as long as the item exists in the original array
      itemIndex < originalItems.length
      ? itemIndex
      : -1;

  if (originalIndex >= 0) {
    return {
      itemAction: 'replace',
      localPath: `${arrayPath}[${originalIndex}]${subPath}`,
      value,
    };
  }

  return {
    itemAction: 'addItem',
    localPath: itemPath,
    value: subPath === '' ? value : (proposedItem ?? null),
  };
};
